import { Component, OnInit } from '@angular/core';
import { NgIcon } from "@ng-icons/core";
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { Account } from '../../shared/account.model';
import { AccountService } from '../../shared/account.service';
import { UserManagementService } from 'src/app/contract/shared/user-management.service';
import * as moment from 'moment';

@Component({
  selector: 'app-account-master',
  standalone: true,
  imports: [
    NgIcon,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatPaginatorModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatCardModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './account-master.component.html',
  styleUrl: './account-master.component.css'
})
export class AccountMasterComponent implements OnInit {
  displayedColumns: string[] = ['name', 'id', 'createdBy', 'createdAt', 'updatedAt', 'status', 'action'];
  dataSource: Account[] = [];
  filteredDataSource: Account[] = [];
  
  // Search and filter
  searchTerm: string = '';
  selectedStatus: string = 'all';
  
  // Pagination
  pageSize = 10;
  pageIndex = 0;
  totalItems = 0;
  pageSizeOptions = [5, 10, 25, 50];
  
  // Sorting
  sortColumn = 'updatedAt';
  sortDirection: 'ASC' | 'DESC' = 'DESC';
  
  // Loading states
  isLoading = false;
  errorMessage = '';
  
  constructor(
    private accountService: AccountService,
    private userManagementService: UserManagementService,
    private router: Router
  ) {}

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    this.isLoading = true;
    this.errorMessage = '';
    
    const tenantId = this.userManagementService.userService.loggedinUser?.tenantId;
    
    this.accountService.getAccounts({
      pageNo: this.pageIndex,
      pageSize: this.pageSize,
      sortBy: this.sortColumn,
      sortDir: this.sortDirection,
      status: this.selectedStatus !== 'all' ? this.selectedStatus as any : undefined,
      tenantId: tenantId?.toString()
    }).subscribe({
      next: (response) => {
        this.dataSource = response.content || [];
        this.totalItems = response.totalElements || 0;
        this.isLoading = false;
      },
      error: (error) => {
        this.errorMessage = 'Failed to load accounts. Please try again.';
        this.isLoading = false;
        console.error('Error loading accounts:', error);
      }
    });
  } 

  applyFilters() {
    // Reset to first page when filtering
    this.pageIndex = 0;
    this.loadData();
  }

  onSearchChange() {
    // Debounce search to avoid too many API calls
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applyFilters();
    }, 500);
  }

  onStatusChange() {
    this.applyFilters();
  }

  private searchTimeout: any;

  onPageChange(event: PageEvent) {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadData(); // Reload data with new pagination
  }

  getPaginatedData(): Account[] {
    // Since API handles pagination, return the data as is
    return this.dataSource;
  }

  onSortChange(column: string) {
    // If clicking the same column, toggle direction
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'ASC' ? 'DESC' : 'ASC';
    } else {
      // If clicking a different column, set it as the new sort column
      this.sortColumn = column;
      this.sortDirection = 'ASC';
    }
    
    // Reset to first page when sorting
    this.pageIndex = 0;
    this.loadData();
  }

  getSortIcon(column: string): string {
    if (this.sortColumn !== column) {
      return 'unfold_more'; // No sort
    }
    return this.sortDirection === 'ASC' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  getStatusColor(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'green';
      case 'DRAFT': return 'orange';
      case 'INACTIVE': return 'red';
      case 'BLACKLISTED': return 'gray';
      default: return 'gray';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'ACTIVE': return 'Active';
      case 'DRAFT': return 'Draft';
      case 'INACTIVE': return 'Inactive';
      case 'BLACKLISTED': return 'Blacklisted';
      default: return status || 'No Status';
    }
  }

  getTypeColor(type: string): string {
    switch (type) {
      case 'CUSTOMER': return 'blue';
      case 'VENDOR': return 'green';
      case 'PARTNER': return 'purple';
      case 'OTHER': return 'gray';
      default: return 'gray';
    }
  }

  getTypeLabel(type: string): string {
    switch (type) {
      case 'CUSTOMER': return 'Customer';
      case 'VENDOR': return 'Vendor';
      case 'PARTNER': return 'Partner';
      case 'OTHER': return 'Other';
      default: return type || 'No Type';
    }
  }

  formatDate(date: Date | string): string {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY h:mm A');
  }

  formatDateOnly(date: Date | string): string {
    if (!date) return '-';
    return moment(date).format('MMM DD, YYYY');
  }

  addAccount() {
    this.router.navigate(['/app/account-master/add']);
  }

  editAccount(account: Account) {
    this.router.navigate(['/app/account-master/edit', account.id]);
  }

  viewAccount(account: Account) {
    this.router.navigate(['/app/account-master/view', account.id]);
  }

  getUserName(userId: number): string {
    if (!userId) return '-';
    
    return userId.toString();
  }

}
